import React, { useEffect } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Button,
  Grid,
  Paper,
  Alert,
  Stack,
} from "@mui/material";
import { useAdminReducer } from "../hooks/useAdminReducer";
import {
  getComingSoonPages,
  updateComingSoonPage,
} from "../actions/comingsoon.actions";
import ToolBody from "../components/common/ToolBody";

function ComingSoon() {
  // Use the shared admin hook
  const { state, setLoading, isLoading, setFormData, setMessage } =
    useAdminReducer({
      formData: {
        pages: [], // Store pages in formData
      },
      // Skip unnecessary state properties for this simple page
      tableData: null,
      sectionData: null,
      images: null,
    });

  const { formData } = state;
  const { pages } = formData;
  const { successMsg, errorMsg } = state;

  useEffect(() => {
    setLoading(true);
    getComingSoonPages()
      .then((response) => {
        if (response.data.status) {
          setFormData({ pages: response.data.data });
        }
      })
      .catch(() => {
        setMessage("", "Failed to load pages");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setFormData, setLoading, setMessage]);

  const handleChange = (e, id) => {
    const updatedPages = pages.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          status: e.target.checked ? "active" : "inactive",
        };
      }
      return item;
    });

    setFormData({ pages: updatedPages });
  };

  const handleSubmit = () => {
    if (isLoading) return;

    setLoading(true);
    setMessage("", "");

    updateComingSoonPage({ pages })
      .then((response) => {
        if (response.data.status) {
          setMessage(
            response.data.message || "Pages updated successfully!",
            "",
          );
          setTimeout(() => {
            setMessage("", "");
          }, 3000);
        } else {
          setMessage("", response.data.message || "Failed to update pages");
        }
      })
      .catch(() => {
        setMessage("", "Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <ToolBody>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Title section */}
          <Typography variant="h5" gutterBottom>
            Coming Soon Page Overrides
          </Typography>

          {/* Description */}
          <Typography variant="body1">
            Check box(es) to enable "Coming Soon" status for the pages
          </Typography>

          {/* Loading indicator */}
          {isLoading && !pages.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Page checkboxes */}
              <Grid container spacing={2}>
                {pages.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.id}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.status === "active"}
                            onChange={(e) => handleChange(e, item.id)}
                          />
                        }
                        label={item.name}
                      />
                    </FormGroup>
                  </Grid>
                ))}
              </Grid>

              {/* Action button */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  endIcon={isLoading && <CircularProgress size={20} />}
                >
                  Update
                </Button>
              </Box>

              {/* Status messages */}
              {successMsg && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {successMsg}
                </Alert>
              )}
              {errorMsg && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errorMsg}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </ToolBody>
  );
}

export default ComingSoon;
